import React from "react";
import PropTypes from "prop-types";
import { Button } from "@aidron/aidron-ds";
import { COLUMNS_LABELS, COLUMNS_IDENTIFIERS, ALLOWED_PURCHASE_STATUS } from "../../components.constants";
import { getSupplierId, calculateEffectivePurchase, getPrefixId } from "../../../utils/utils";

const createMockRow = (rowData) => ({
  original: rowData,
  getValue: (id) => rowData[id]
});

const processRowForBatchUpdate = (rowData, columnId, inputs, suppliersAndProductRelations) => {
  const productId = rowData.productId;
  const storeId = rowData.storeId;
  const cellValue = rowData[columnId];
  const numericValue = Number(cellValue);

  if (isNaN(numericValue) || numericValue < 0) return { error: true, productId, reason: 'invalid_value' };

  try {
    const mockRow = createMockRow(rowData);

    const resolvedSupplierId = getSupplierId(mockRow, inputs, productId, suppliersAndProductRelations, false);

    const packToUnitRatio = inputs?.[productId]?.packToUnitRatio || rowData.packToUnitRatio;
    const effectivePurchaseValue = calculateEffectivePurchase(numericValue, packToUnitRatio);

    if (effectivePurchaseValue === 0 && numericValue > 0) {
      console.warn('')
    }

    return {
      error: false,
      productId,
      data: {
        ...inputs?.[productId],
        [COLUMNS_IDENTIFIERS.EFFECTIVE_PURCHASE]: effectivePurchaseValue,
        [columnId]: numericValue,
        source: columnId,
        prefixId: getPrefixId(mockRow, inputs, productId),
        purchaseDate: rowData?.purchaseDate,
        purchaseRecommendationId: rowData.purchaseRecommendationId,
        storeId: storeId ?? 0,
        supplierId: resolvedSupplierId,
        purchaseStatus: ALLOWED_PURCHASE_STATUS.EDITED,
      }
    };
  } catch (e) {
    console.error(`Erro ao processar produto ${productId}:`, e.message);
    return { error: true, productId, reason: 'no_supplier' };
  }
};

const applyBatchUpdate = (columnId, filteredData, inputs, suppliersAndProductRelations, setInputs) => {
  if (!columnId || !filteredData?.length || !setInputs) {
    console.warn("Dados insuficientes para batch update: ", { setInputs });
    return;
  }

  const batchUpdates = {};
  const errors = { invalid_value: [], no_supplier: [], other: [] };

  // Sem .filter() — processRowForBatchUpdate já rejeita NaN e negativos
  filteredData.forEach((rowData) => {
    const result = processRowForBatchUpdate(rowData, columnId, inputs, suppliersAndProductRelations);
    if (result?.error) {
      errors[result.reason || 'other'].push(result.productId);
    } else {
      batchUpdates[result.productId] = result.data;
    }
  });

  const updatedCount = Object.keys(batchUpdates).length;
  if (updatedCount > 0) {
    setInputs(prev => ({ ...prev, ...batchUpdates }));
  } else {
    console.warn("Nenhum produto foi atualizado. Verifique os erros acima.");
  }
};

export const SubHeader = React.memo(({ actions, ctx, uiState, }) => {

  const { setInputs } = actions || {};

  const { 
    column, 
    title, 
    tooltip, 
    suppliersAndProductRelations,
    filteredData = [],
  } = ctx || {};

  const {
    sorting = [],
    showSortButton = false,
    inputs = {},
    updateBacthCondition,
    headerFontSize
  } = uiState || {};

  const largerTitle = [
    COLUMNS_LABELS.SUPPLIER,
    COLUMNS_LABELS.SUBGROUP,
    COLUMNS_LABELS.PRODUCT,
    COLUMNS_LABELS.PHYSICAL_STOCK,
  ];

  const fontSize = headerFontSize 
    ? headerFontSize 
    : (largerTitle.includes(title) ? "0.7rem" : "0.6rem");

  const sortState = sorting?.find((s) => s.id === column?.id);

  const handleBatchUpdate = React.useCallback(() => {
    applyBatchUpdate(
      column?.id,
      filteredData,
      inputs,
      suppliersAndProductRelations,
      setInputs,
    ); 
  }, [column?.id, filteredData, inputs, suppliersAndProductRelations, setInputs]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginBlockEnd: showSortButton ? "" : "0.8rem",
      }}
      title={tooltip}
    >
      <span style={{ fontSize, margin: "0 0 0.3rem 0" }}>{title}</span>

      {showSortButton && (
        <div style={{ margin: "0 -0.6rem", transform: "scale(0.40)" }}>
          <Button
            icon={sortState?.desc ? "arrow-down" : "arrow-up"}
            type="ghost"
            size="lg"
          />
        </div>
      )}

      {updateBacthCondition && (
        <div style={{ margin: "0 -0.6rem", transform: "scale(0.40)" }}>
          <Button
            icon={"target"}
            type="ghost"
            size="lg"
            onClick={(e) => {
              e.stopPropagation();
              handleBatchUpdate();
            }}
          />
        </div>
      )}
    </div>
  );
});

SubHeader.propTypes = { ctx: PropTypes.shape(), uiState: PropTypes.shape() };